import { EditorialSpreadPublicationControlsV1 } from "./EditorialSpreadPublicationControlsV1";
/**
 * Editorial Authoring V2 — HERO smart authoring (BUILD 2, final data
 * safety + Reader fidelity correction).
 *
 * Rendered by EditorialSpreadWorkspaceV2.jsx in place of the generic
 * workspace body when block.master_type === "HERO". Self-contained:
 * owns its own modal chrome, save flow and save-state display.
 *
 * No import from @/components/editorial/ (the legacy authoring UI).
 * Allowed imports only: @/lib/*, and this app's own
 * @/components/editorial-v2/* siblings.
 *
 * RECOGNITION: deterministic only, via the existing
 * structureHeroSource() in @/lib/editorialHeroAuthoring.mjs. No
 * provider call. Provider calls = 0, AI cost = $0.
 *
 * ── V2 SHELL → HERO TEXT BRIDGE (unchanged from prior correction) ──
 * deriveInitialHeroText() resolves content.body (freshest V2 shell
 * input) against content.extra.hero_source_text + the legacy
 * eyebrow/headline/subtitle/lead fields, without silently ignoring a
 * fresh edit or falsely treating unrecognized text as already
 * structured.
 *
 * ── SAVE SAFETY (this correction) ───────────────────────────────────
 * If the author has typed/pasted a new or changed source
 * (sourceText.trim() is non-empty and recognized is still null — i.e.
 * "Распознать текст" hasn't been run on it yet), Save is disabled and
 * a calm caption explains why. save() itself also guards against
 * running in that state. Critically, when recognized is null, the
 * structured fields (eyebrow/headline/subtitle/lead) are simply left
 * out of the patch entirely — content's existing values pass through
 * via the `...c` spread untouched. There is no more fallback that
 * turns "not yet recognized" into empty structured fields; a media-
 * only block with no source text at all can still Save normally,
 * since in that case there is nothing to accidentally overwrite.
 *
 * ── V2 SHELL → HERO MEDIA BRIDGE (unchanged from prior correction) ──
 * deriveInitialHeroMedia() prioritizes content.media_ids[0], falling
 * back to content.hero_media_id, resolving an unknown URL via the
 * existing GET /admin/media + mediaRecordUrl.
 *
 * ── MOBILE OVERRIDE CLEARING (this correction) ──────────────────────
 * V2's contract is one primary HERO photo for both Desktop and
 * Mobile, with independent crop per device — never a second Mobile-
 * only photo. Older blocks may still carry a legacy
 * hero_mobile_media_id/hero_mobile_url, which the real Reader would
 * prefer over the primary photo, causing Reader and V2 to disagree.
 * Every successful HERO V2 Save now explicitly clears both
 * (hero_mobile_media_id: null, hero_mobile_url: null) while leaving
 * the mobile crop settings (settings.mobile_focal_x/mobile_focal_y/
 * hero_mobile_zoom) untouched — one media, independent per-device
 * crop, exactly as intended.
 *
 * ── READER-FAITHFUL PREVIEW (this correction) ───────────────────────
 * "Предпросмотр разворота" mirrors the actual current HeroRender
 * contract: settings.variant ("A" | "B" | "C", default "A") drives the
 * Desktop composition (A: image left / text right, B: text left /
 * image right, C: full-bleed image with a left-to-right dark→
 * transparent gradient and text in a safe left column), and Mobile
 * shows the primary 3:4 photo with a soft fade into the page
 * background, then the same editorial text stack. Colors come from
 * V2-only token maps (PALETTES / BACKGROUNDS below) that mirror the
 * Reader's current palette/background key → hex values, so the
 * preview does not need to import HeroMaster or anything else from
 * @/components/editorial/ to stay visually faithful.
 *
 * EXISTING FIELDS REUSED (no new backend fields, no migration):
 *   content.body, content.eyebrow, content.headline, content.subtitle,
 *   content.lead, content.media_ids, content.hero_media_id,
 *   content.hero_url, content.hero_mobile_media_id (cleared on save),
 *   content.hero_mobile_url (cleared on save),
 *   content.extra.hero_source_text, content.extra.hero_leftover_text
 *   settings.variant, settings.palette, settings.background,
 *   settings.focal_x, settings.focal_y, settings.hero_zoom (desktop
 *   crop), settings.mobile_focal_x, settings.mobile_focal_y,
 *   settings.hero_mobile_zoom (mobile crop)
 */
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { structureHeroSource } from "@/lib/editorialHeroAuthoring.mjs";
import { editorialMediaFrameAspect, editorialMediaFrameLabel } from "@/lib/editorialMediaFrame.mjs";
import { mediaRecordUrl } from "@/lib/editorialMedia.mjs";
import { MediaLibraryPickerV2, MEDIA_LIST, MEDIA_UPLOAD } from "./EditorialMediaPickerV2";
import { EditorialCropV2 } from "./EditorialCropV2";
import { PublicationDestinationV2 } from "./PublicationDestinationV2";

// A short list of common short Russian one/two-letter prepositions and
// conjunctions that should not be left hanging alone at a line end.
// Presentational only — applied to the local preview's text, never to
// what gets saved.
const NBSP_WORDS = ["и", "в", "к", "с", "у", "о", "а", "на", "до", "из", "от", "по", "за", "но", "что", "как", "не"];
function typographicNbsp(text) {
  if (!text) return text;
  const pattern = new RegExp(`(^|\\s)(${NBSP_WORDS.join("|")})\\s+`, "giu");
  return text.replace(pattern, (m, pre, word) => `${pre}${word}\u00A0`);
}

// V2 shell → HERO text bridge. See file header for the full contract.
function deriveInitialHeroText(content) {
  const c = content || {};
  const bodySource = String(c.body || "");
  const storedHeroSource = String(c.extra?.hero_source_text || "");
  const initialSource = bodySource.trim() ? bodySource : storedHeroSource;

  const hasLegacyFields = Boolean(c.eyebrow || c.headline || c.subtitle || c.lead);
  const isNewOrChangedSource = Boolean(bodySource.trim()) && bodySource.trim() !== storedHeroSource.trim();

  if (!isNewOrChangedSource && hasLegacyFields) {
    return {
      sourceText: initialSource,
      recognized: {
        eyebrow: c.eyebrow || "", headline: c.headline || "",
        subtitle: c.subtitle || "", lead: c.lead || "",
      },
      leftoverText: c.extra?.hero_leftover_text || "",
    };
  }

  return {
    sourceText: initialSource,
    recognized: null,
    // A genuinely new/changed body has no recognized leftover yet —
    // don't show a stale leftover left over from a different source.
    leftoverText: isNewOrChangedSource ? "" : (c.extra?.hero_leftover_text || ""),
  };
}

// V2 shell → HERO media bridge. See file header for the full contract.
function deriveInitialHeroMedia(content) {
  const c = content || {};
  const mediaId = (Array.isArray(c.media_ids) && c.media_ids[0]) || c.hero_media_id || null;
  // Only trust content.hero_url as the URL for this id when it's the
  // same id hero_url was actually saved for; otherwise it must be
  // resolved against the media list (handled by an effect below).
  const mediaUrl = (mediaId && mediaId === c.hero_media_id) ? (c.hero_url || null) : null;
  return { mediaId, mediaUrl };
}

// ── Reader design tokens (V2-only mirror, not an import) ────────────
// These hex values mirror the current HeroRender palette/background
// key → color contract exactly, so the V2 preview matches the Reader
// without importing anything from @/components/editorial/.
const PALETTES = {
  ivory: "#EFE6D5",
  almond: "#D6B98A",
  olive: "#7E8A3E",
  cocoa: "#5B3B25",
  burgundy: "#7A2A2A",
  gold: "#C9A84C",
  pistachio: "#A6C088",
  apricot: "#E8B48A",
};
const BACKGROUNDS = {
  "warm-ivory": "#EFE6D5",
  "mineral-plaster": "#E6DFCF",
  "almond-paper": "#F1E7D2",
  "deep-cocoa": "#2A1810",
  "full-image": "transparent",
};
const TEXT_DARK = "#3B241A";
const TEXT_LIGHT = "#F1E4CB";

function paletteColor(key) { return PALETTES[key] || PALETTES.ivory; }
function backgroundColor(key) { return BACKGROUNDS[key] || BACKGROUNDS["warm-ivory"]; }
function textColorFor(backgroundKey) { return backgroundKey === "deep-cocoa" ? TEXT_LIGHT : TEXT_DARK; }

function HeroTextBlock({ eyebrow, headline, subtitle, lead, textColor, accentColor }) {
  return (
    <div>
      {eyebrow && (
        <div className="text-[10px] uppercase tracking-widest opacity-60" style={{ color: textColor }}>
          {eyebrow}
        </div>
      )}
      {headline && (
        <div className="mt-1 font-serif text-xl leading-snug" style={{ color: textColor }}>
          {typographicNbsp(headline)}
        </div>
      )}
      {accentColor && (headline || subtitle) && (
        <div className="mt-2 h-[2px] w-10" style={{ backgroundColor: accentColor }} />
      )}
      {subtitle && (
        <div className="mt-2 font-serif text-sm opacity-90" style={{ color: textColor }}>
          {typographicNbsp(subtitle)}
        </div>
      )}
      {lead && (
        <div className="mt-1 text-xs opacity-70" style={{ color: textColor }}>
          {typographicNbsp(lead)}
        </div>
      )}
    </div>
  );
}

function HeroPreviewPhoto({ mediaUrl, crop, aspect, rounded = true, children }) {
  return (
    <div className={`relative overflow-hidden bg-espresso/10 ${rounded ? "rounded-xl" : ""}`} style={{ aspectRatio: aspect }}>
      {mediaUrl && (
        <img src={mediaUrl} alt="" className="absolute inset-0 h-full w-full"
          style={{
            objectFit: "cover",
            objectPosition: `${crop.focal_x * 100}% ${crop.focal_y * 100}%`,
            transform: `scale(${crop.zoom})`,
            transformOrigin: `${crop.focal_x * 100}% ${crop.focal_y * 100}%`,
          }} />
      )}
      {children}
    </div>
  );
}

export function EditorialHeroV2({ block, volume, onClose, onSaved, publicationDirty, onPublicationInput, onContinueInStory }) {
  const mediaAccessZone = volume?.product_id ? "paid" : "public";
  const c = block.content || {};
  const s = block.settings || {};

  const initialText = deriveInitialHeroText(c);
  const initialMedia = deriveInitialHeroMedia(c);

  const [sourceText, setSourceText] = useState(initialText.sourceText);
  const [recognized, setRecognized] = useState(initialText.recognized);
  const [leftoverText, setLeftoverText] = useState(initialText.leftoverText);
  const [editingFields, setEditingFields] = useState(false);
  // QA FIX: lets a recognized HERO reopen its raw source without
  // losing the recognized result just by opening it. sourceEditing
  // only controls textarea visibility; recognizedSourceText is a
  // transient snapshot of the source that produced the current
  // recognized result, compared against sourceText to decide whether
  // an actual change happened (not persisted anywhere).
  const [sourceEditing, setSourceEditing] = useState(false);
  const [recognizedSourceText, setRecognizedSourceText] = useState(
    initialText.recognized ? initialText.sourceText : ""
  );

  const [mediaId, setMediaId] = useState(initialMedia.mediaId);
  const [mediaUrl, setMediaUrl] = useState(initialMedia.mediaUrl);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [desktopCrop, setDesktopCrop] = useState({
    focal_x: s.focal_x ?? 0.5, focal_y: s.focal_y ?? 0.5, zoom: s.hero_zoom ?? 1,
  });
  const [mobileCrop, setMobileCrop] = useState({
    focal_x: s.mobile_focal_x ?? s.focal_x ?? 0.5,
    focal_y: s.mobile_focal_y ?? s.focal_y ?? 0.5,
    zoom: s.hero_mobile_zoom ?? s.hero_zoom ?? 1,
  });
  const [cropOpen, setCropOpen] = useState(null); // null | "desktop" | "mobile"

  const [showPreview, setShowPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop"); // desktop|mobile
  const [saveState, setSaveState] = useState("idle"); // idle|saving|saved|error

  useEffect(() => {
    const nextText = deriveInitialHeroText(block.content || {});
    const nextMedia = deriveInitialHeroMedia(block.content || {});
    const nextSettings = block.settings || {};

    setSourceText(nextText.sourceText);
    setRecognized(nextText.recognized);
    setLeftoverText(nextText.leftoverText);
    setEditingFields(false);
    setSourceEditing(false);
    setRecognizedSourceText(nextText.recognized ? nextText.sourceText : "");

    setMediaId(nextMedia.mediaId);
    setMediaUrl(nextMedia.mediaUrl);

    setDesktopCrop({
      focal_x: nextSettings.focal_x ?? 0.5, focal_y: nextSettings.focal_y ?? 0.5, zoom: nextSettings.hero_zoom ?? 1,
    });
    setMobileCrop({
      focal_x: nextSettings.mobile_focal_x ?? nextSettings.focal_x ?? 0.5,
      focal_y: nextSettings.mobile_focal_y ?? nextSettings.focal_y ?? 0.5,
      zoom: nextSettings.hero_mobile_zoom ?? nextSettings.hero_zoom ?? 1,
    });
    setSaveState("idle");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id]);

  // Resolve a media_ids-derived id that has no already-known URL
  // (see deriveInitialHeroMedia) via the existing media list endpoint.
  useEffect(() => {
    if (!mediaId || mediaUrl) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(MEDIA_LIST);
        if (cancelled) return;
        const records = Array.isArray(data) ? data : (data?.items || data?.results || []);
        const record = records.find((r) => r.id === mediaId);
        const url = record ? mediaRecordUrl(record) : null;
        if (url) setMediaUrl(url);
      } catch {
        // Non-fatal: thumbnail/preview stays empty until the author picks again.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id, mediaId]);  const heroVariant = s.variant || "A";
  const heroPalette = s.palette || "ivory";
  const heroBackground = s.background || "warm-ivory";
  const desktopAspect = editorialMediaFrameAspect("HERO", { mode: "desktop", variant: heroVariant });
  const mobileAspect = editorialMediaFrameAspect("HERO", { mode: "mobile", variant: heroVariant });
  const desktopFrameLabel = editorialMediaFrameLabel("HERO", { mode: "desktop", variant: heroVariant });
  const mobileFrameLabel = editorialMediaFrameLabel("HERO", { mode: "mobile", variant: heroVariant });

  // Save safety: a source the author changed/pasted but hasn't run
  // through recognition yet must never overwrite the stored structured
  // fields with blanks. Save is disabled for this exact state.
  const sourceNeedsRecognition = Boolean(sourceText.trim()) && !recognized;

  const recognize = () => {
    const result = structureHeroSource(sourceText);
    setRecognized({
      eyebrow: result.eyebrow, headline: result.headline,
      subtitle: result.subtitle, lead: result.lead,
    });
    setLeftoverText(result.leftoverText || "");
    setRecognizedSourceText(sourceText);
    setEditingFields(false);
    setSourceEditing(false);
  };

  // QA FIX: typing in the reopened source textarea only invalidates
  // the current recognized result once the text actually differs from
  // the source that produced it — merely opening "Изменить исходный
  // текст" must not blank anything. Once it does differ, no stale
  // leftover from the old source may survive (a new "Распознать
  // текст" is required, which will regenerate leftoverText fresh).
  const handleSourceTextChange = (value) => {
    setSourceText(value);
    if (recognized && value.trim() !== recognizedSourceText.trim()) {
      setRecognized(null);
      setLeftoverText("");
    }
  };

  const returnToRecognized = () => {
    setSourceEditing(false);
  };

  const updateField = (field, value) => {
    setRecognized((r) => ({ ...(r || { eyebrow: "", headline: "", subtitle: "", lead: "" }), [field]: value }));
  };

  const pickFromLibrary = (media) => {
    const url = mediaRecordUrl(media) || media.file_url || media.url || null;
    setMediaId(media.id);
    setMediaUrl(url);
    setLibraryOpen(false);
  };

  const removePhoto = () => {
    setMediaId(null);
    setMediaUrl(null);
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("access_zone", mediaAccessZone);
      const { data } = await api.post(MEDIA_UPLOAD, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = mediaRecordUrl(data) || data.file_url || data.url || null;
      setMediaId(data.id);
      setMediaUrl(url);
      toast.success("Фото загружено");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const saveCrop = (device, values) => {
    if (device === "desktop") setDesktopCrop(values);
    else setMobileCrop(values);
    setCropOpen(null);
  };

  const continueInStory = () => {
    // BUILD 3: real handoff. This component has no visibility into
    // sibling blocks (only the page does), so target selection,
    // "no target found" messaging, and opening the target workspace
    // all happen in EditorialV2.jsx via onContinueInStory. The HERO
    // leftover itself (content.extra.hero_leftover_text) is untouched
    // either way — nothing here deletes or consumes it.
    //
    // leftoverText (local state) is passed explicitly alongside block:
    // if the author just ran "Распознать текст" and hasn't pressed
    // HERO's own Save yet, block.content.extra.hero_leftover_text is
    // still the OLD saved value — leftoverText is the current one.
    onContinueInStory?.(block, leftoverText);
  };

  const save = async () => {
    // Belt-and-braces guard mirroring the disabled Save button: never
    // persist while a changed source is still unrecognized.
    if (sourceNeedsRecognition) return;

    setSaveState("saving");
    try {
      // Only overwrite the structured fields when we actually have a
      // recognized result. When recognized is null (media-only block,
      // no source text at all), these keys are simply omitted here —
      // the `...c` spread below preserves whatever was already saved,
      // instead of a fallback that would blank them out.
      const structuredPatch = recognized
        ? {
          eyebrow: recognized.eyebrow,
          headline: recognized.headline,
          subtitle: recognized.subtitle,
          lead: recognized.lead,
        }
        : {};

      const nextContent = {
        ...c,
        ...structuredPatch,
        body: sourceText,
        hero_media_id: mediaId,
        hero_url: mediaUrl,
        media_ids: mediaId ? [mediaId] : [],
        // V2's contract is one primary photo for Desktop + Mobile —
        // clear any legacy Mobile-only override so the real Reader
        // can't prefer it over hero_url after this Save.
        hero_mobile_media_id: null,
        hero_mobile_url: null,
        extra: {
          ...(c.extra || {}),
          hero_source_text: sourceText,
          hero_leftover_text: leftoverText,
        },
      };
      const nextSettings = {
        ...s,
        focal_x: desktopCrop.focal_x,
        focal_y: desktopCrop.focal_y,
        hero_zoom: desktopCrop.zoom,
        // Mobile crop settings are kept — only the legacy Mobile photo
        // override above is cleared, never the crop itself.
        mobile_focal_x: mobileCrop.focal_x,
        mobile_focal_y: mobileCrop.focal_y,
        hero_mobile_zoom: mobileCrop.zoom,
      };
      await api.patch(`/admin/editorial/blocks/${block.id}`, { content: nextContent, settings: nextSettings });
      setSaveState("saved");
      const savedMediaItems = mediaId && mediaUrl ? [{ id: mediaId, url: mediaUrl }] : [];
      onSaved?.({ ...block, content: nextContent, settings: nextSettings }, savedMediaItems);
      toast.success("Изменения сохранены");
      setTimeout(() => setSaveState("idle"), 1500);
    } catch (e) {
      setSaveState("error");
      toast.error(formatApiError(e));
    }
  };

  const saveLabel = {
    idle: "", saving: "Сохранение…", saved: "Сохранено", error: "Ошибка сохранения",
  }[saveState];

  const title = recognized?.headline || c.headline || "Обложка";

  return (
    <div
      onInputCapture={onPublicationInput} onChangeCapture={onPublicationInput}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="editorial-v2-hero-workspace"
    >
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl bg-cream">
        <div className="flex items-start justify-between gap-3 border-b border-espresso/10 px-5 py-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-espresso/55">Обложка</div>
            <div className="mt-0.5 truncate font-serif text-xl text-espresso">{title}</div>
          </div>
          {saveLabel && (
            <span className="shrink-0 pt-1 text-[11px] uppercase tracking-widest text-espresso/50"
              data-testid="editorial-v2-hero-save-state">
              {saveLabel}
            </span>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          <PublicationDestinationV2 materialId={block.material_id} volume={volume} />

          {/* Text input — shown before recognition, or when the author
              explicitly reopens it via "Изменить исходный текст". */}
          {(!recognized || sourceEditing) && (
            <div>
              <label className="mb-1 block text-[11px] text-espresso/65">
                {recognized ? "Исходный текст" : "Вставить текст"}
              </label>
              <textarea
                value={sourceText}
                onChange={(e) => handleSourceTextChange(e.target.value)}
                rows={8}
                placeholder={"БИСКВИТЫ · 01\nPain de Gênes\nВлажный миндальный бисквит\nКлассический французский бисквит..."}
                className="w-full rounded-xl border border-espresso/20 bg-white px-3 py-3 text-sm leading-relaxed outline-none focus:border-espresso/45"
                data-testid="editorial-v2-hero-text"
              />
              {recognized && sourceEditing && (
                <button type="button" onClick={returnToRecognized}
                  className="mt-2 text-xs text-espresso/60 underline underline-offset-2"
                  data-testid="editorial-v2-hero-return-to-recognized">
                  Вернуться к подготовленному тексту
                </button>
              )}
            </div>
          )}

          {/* Photo controls */}
          <div>
            {mediaUrl ? (
              <div className="flex items-start gap-3">
                <img src={mediaUrl} alt="" className="h-16 w-16 shrink-0 rounded-lg object-cover"
                  style={{ objectPosition: `${desktopCrop.focal_x * 100}% ${desktopCrop.focal_y * 100}%` }} />
                <div className="flex flex-1 flex-wrap gap-2">
                  <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                    className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso disabled:opacity-50"
                    data-testid="editorial-v2-hero-replace-photo">
                    {uploading ? "Загрузка…" : "Заменить фото"}
                  </button>
                  <button type="button" onClick={() => setLibraryOpen(true)}
                    className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso"
                    data-testid="editorial-v2-hero-open-library">
                    Из Медиатеки
                  </button>
                  <button type="button" onClick={() => setCropOpen("desktop")}
                    className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso"
                    data-testid="editorial-v2-hero-crop-desktop">
                    Настроить кадр
                  </button>
                  <button type="button" onClick={removePhoto}
                    className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso/70"
                    data-testid="editorial-v2-hero-remove-photo">
                    Убрать фото
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso disabled:opacity-50"
                  data-testid="editorial-v2-hero-add-photo">
                  {uploading ? "Загрузка…" : "Добавить фотографию"}
                </button>
                <button type="button" onClick={() => setLibraryOpen(true)}
                  className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso"
                  data-testid="editorial-v2-hero-open-library-empty">
                  Из Медиатеки
                </button>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileSelected}
              data-testid="editorial-v2-hero-file-input"
            />

            {mediaUrl && (
              <div className="mt-3 rounded-xl border border-espresso/10 bg-white/60 px-3 py-3 text-sm text-espresso/70">
                <div className="font-medium text-espresso">Mobile</div>
                <div className="mt-0.5">Используется основное фото</div>
                <button type="button" onClick={() => setCropOpen("mobile")}
                  className="mt-2 rounded-lg border border-espresso/20 bg-white px-3 py-1.5 text-xs"
                  data-testid="editorial-v2-hero-crop-mobile">
                  Настроить мобильный кадр
                </button>
              </div>
            )}
          </div>

          {/* Recognize action, only before a result exists */}
          {!recognized && (
            <div>
              <button type="button" onClick={recognize} disabled={!sourceText.trim()}
                className="rounded-xl bg-espresso px-5 py-2.5 text-sm text-cream disabled:opacity-40"
                data-testid="editorial-v2-hero-recognize">
                Распознать текст
              </button>
              <p className="mt-2 text-xs text-espresso/60">
                Редакция сама подготовит структуру этого разворота.
              </p>
            </div>
          )}

          {/* Result after recognition */}
          {recognized && (
            <div className="rounded-2xl border border-espresso/15 bg-white/70 px-4 py-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-medium text-espresso">
                  <span aria-hidden="true">✓</span>
                  <span>Текст подготовлен</span>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button type="button" onClick={() => setEditingFields((v) => !v)}
                    className="text-xs text-espresso/60 underline underline-offset-2"
                    data-testid="editorial-v2-hero-edit-toggle">
                    {editingFields ? "Свернуть" : "Редактировать текст"}
                  </button>
                  <button type="button" onClick={() => setSourceEditing(true)}
                    className="text-xs text-espresso/60 underline underline-offset-2"
                    data-testid="editorial-v2-hero-edit-source">
                    Изменить исходный текст
                  </button>
                </div>
              </div>

              {!editingFields ? (
                <div className="mt-3 space-y-1">
                  {recognized.eyebrow && (
                    <div className="text-[11px] uppercase tracking-widest text-espresso/50">{recognized.eyebrow}</div>
                  )}
                  {recognized.headline && (
                    <div className="font-serif text-lg text-espresso">{recognized.headline}</div>
                  )}
                  {recognized.subtitle && (
                    <div className="text-sm text-espresso/70">{recognized.subtitle}</div>
                  )}
                  {recognized.lead && (
                    <div className="text-sm text-espresso/60">{recognized.lead}</div>
                  )}
                  {!recognized.eyebrow && !recognized.headline && !recognized.subtitle && !recognized.lead && (
                    <div className="text-sm italic text-espresso/50">Пока пусто — нажмите «Редактировать текст».</div>
                  )}
                </div>
              ) : (
                <div className="mt-3 grid gap-3">
                  <div>
                    <label className="mb-1 block text-[11px] text-espresso/60">Надзаголовок</label>
                    <input value={recognized.eyebrow} onChange={(e) => updateField("eyebrow", e.target.value)}
                      className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                      data-testid="editorial-v2-hero-field-eyebrow" />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] text-espresso/60">Заголовок</label>
                    <input value={recognized.headline} onChange={(e) => updateField("headline", e.target.value)}
                      className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                      data-testid="editorial-v2-hero-field-headline" />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] text-espresso/60">Подзаголовок</label>
                    <input value={recognized.subtitle} onChange={(e) => updateField("subtitle", e.target.value)}
                      className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                      data-testid="editorial-v2-hero-field-subtitle" />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] text-espresso/60">Короткий текст</label>
                    <textarea value={recognized.lead} onChange={(e) => updateField("lead", e.target.value)} rows={3}
                      className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                      data-testid="editorial-v2-hero-field-lead" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Leftover */}
          {leftoverText && (
            <div className="rounded-xl border border-espresso/10 bg-cream/60 px-4 py-3">
              <div className="text-sm text-espresso/75">
                После обложки остался основной текст материала.
              </div>
              <button type="button" onClick={continueInStory}
                className="mt-2 rounded-lg border border-espresso/20 bg-white px-3 py-1.5 text-xs text-espresso"
                data-testid="editorial-v2-hero-continue-story">
                Продолжить в редакционной истории
              </button>
            </div>
          )}

          {/* Local HERO preview — mirrors the actual Reader contract:
              settings.variant A/B/C on Desktop, the same palette/
              background tokens, and the current Mobile composition. */}
          {showPreview && (
            <div className="rounded-2xl border border-espresso/15 bg-white p-4" data-testid="editorial-v2-hero-preview">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-espresso/50">Предпросмотр разворота</div>
                <div className="flex gap-1 rounded-full border border-espresso/15 p-0.5 text-[11px]">
                  <button type="button" onClick={() => setPreviewDevice("desktop")}
                    className={`rounded-full px-2.5 py-1 ${previewDevice === "desktop" ? "bg-espresso text-cream" : "text-espresso/60"}`}
                    data-testid="editorial-v2-hero-preview-desktop">
                    Desktop
                  </button>
                  <button type="button" onClick={() => setPreviewDevice("mobile")}
                    className={`rounded-full px-2.5 py-1 ${previewDevice === "mobile" ? "bg-espresso text-cream" : "text-espresso/60"}`}
                    data-testid="editorial-v2-hero-preview-mobile">
                    Mobile
                  </button>
                </div>
              </div>

              {previewDevice === "desktop" ? (
                <div className="mx-auto max-w-lg" data-testid={`editorial-v2-hero-preview-variant-${heroVariant}`}>
                  {heroVariant === "C" ? (
                    <HeroPreviewPhoto mediaUrl={mediaUrl} crop={desktopCrop} aspect={desktopAspect}>
                      <div className="pointer-events-none absolute inset-0" style={{
                        background: "linear-gradient(to right, rgba(0,0,0,0.75), rgba(0,0,0,0.35) 45%, transparent 75%)",
                      }} />
                      <div className="absolute inset-y-0 left-0 flex w-[55%] items-center px-6">
                        <HeroTextBlock eyebrow={recognized?.eyebrow} headline={recognized?.headline}
                          subtitle={recognized?.subtitle} lead={recognized?.lead}
                          textColor={TEXT_LIGHT} accentColor={paletteColor(heroPalette)} />
                      </div>
                    </HeroPreviewPhoto>
                  ) : (
                    <div className="grid grid-cols-2 overflow-hidden rounded-2xl" style={{ backgroundColor: backgroundColor(heroBackground) }}>
                      {heroVariant === "B" ? (
                        <>
                          <div className="flex items-center p-5">
                            <HeroTextBlock eyebrow={recognized?.eyebrow} headline={recognized?.headline}
                              subtitle={recognized?.subtitle} lead={recognized?.lead}
                              textColor={textColorFor(heroBackground)} accentColor={paletteColor(heroPalette)} />
                          </div>
                          <HeroPreviewPhoto mediaUrl={mediaUrl} crop={desktopCrop} aspect={desktopAspect} rounded={false} />
                        </>
                      ) : (
                        <>
                          <HeroPreviewPhoto mediaUrl={mediaUrl} crop={desktopCrop} aspect={desktopAspect} rounded={false} />
                          <div className="flex items-center p-5">
                            <HeroTextBlock eyebrow={recognized?.eyebrow} headline={recognized?.headline}
                              subtitle={recognized?.subtitle} lead={recognized?.lead}
                              textColor={textColorFor(heroBackground)} accentColor={paletteColor(heroPalette)} />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="mx-auto max-w-[220px] overflow-hidden rounded-2xl">
                  <HeroPreviewPhoto mediaUrl={mediaUrl} crop={mobileCrop} aspect={mobileAspect} rounded={false}>
                    {/* soft transition image → page background */}
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3" style={{
                      background: `linear-gradient(to bottom, transparent, ${backgroundColor(heroBackground)})`,
                    }} />
                    {recognized?.eyebrow && (
                      <div className="absolute inset-x-0 bottom-2 px-3 text-center text-[10px] uppercase tracking-widest"
                        style={{ color: textColorFor(heroBackground) }}>
                        {recognized.eyebrow}
                      </div>
                    )}
                  </HeroPreviewPhoto>
                  <div className="px-3 pb-4 pt-3 text-center" style={{ backgroundColor: backgroundColor(heroBackground) }}>
                    {recognized?.headline && (
                      <div className="font-serif text-lg leading-snug" style={{ color: textColorFor(heroBackground) }}>
                        {typographicNbsp(recognized.headline)}
                      </div>
                    )}
                    {(recognized?.headline || recognized?.subtitle) && (
                      <div className="mx-auto mt-2 h-[2px] w-10" style={{ backgroundColor: paletteColor(heroPalette) }} />
                    )}
                    {recognized?.subtitle && (
                      <div className="mt-2 font-serif text-sm opacity-90" style={{ color: textColorFor(heroBackground) }}>
                        {typographicNbsp(recognized.subtitle)}
                      </div>
                    )}
                    {recognized?.lead && (
                      <div className="mt-1 text-xs opacity-70" style={{ color: textColorFor(heroBackground) }}>
                        {typographicNbsp(recognized.lead)}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {sourceNeedsRecognition && (
          <div className="border-t border-espresso/10 bg-cream/70 px-5 py-2 text-center text-xs text-espresso/60"
            data-testid="editorial-v2-hero-save-guard">
            Сначала распознайте новый текст.
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-espresso/10 px-5 py-4">
          {volume?.slug !== "open-library" && volume?.slug !== "open-library-author-intro" && <EditorialSpreadPublicationControlsV1 block={block} dirty={publicationDirty} />}
          <button type="button" onClick={onClose}
            className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso"
            data-testid="editorial-v2-hero-close">
            Закрыть
          </button>
          <button type="button" onClick={() => setShowPreview((v) => !v)}
            className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso"
            data-testid="editorial-v2-hero-preview-toggle">
            {showPreview ? "Скрыть предпросмотр" : "Предпросмотр разворота"}
          </button>
          <button type="button" onClick={save} disabled={saveState === "saving" || sourceNeedsRecognition}
            className="rounded-xl bg-espresso px-4 py-2 text-sm text-cream disabled:opacity-50"
            data-testid="editorial-v2-hero-save">
            Сохранить изменения
          </button>
        </div>
      </div>

      <MediaLibraryPickerV2 open={libraryOpen} onClose={() => setLibraryOpen(false)} onPick={pickFromLibrary} accessZone={mediaAccessZone} />

      <EditorialCropV2
        open={cropOpen === "desktop"}
        url={mediaUrl}
        aspectRatio={desktopAspect}
        frameLabel={desktopFrameLabel}
        focalX={desktopCrop.focal_x}
        focalY={desktopCrop.focal_y}
        zoom={desktopCrop.zoom}
        onSave={(values) => saveCrop("desktop", values)}
        onClose={() => setCropOpen(null)}
      />
      <EditorialCropV2
        open={cropOpen === "mobile"}
        url={mediaUrl}
        aspectRatio={mobileAspect}
        frameLabel={mobileFrameLabel}
        focalX={mobileCrop.focal_x}
        focalY={mobileCrop.focal_y}
        zoom={mobileCrop.zoom}
        onSave={(values) => saveCrop("mobile", values)}
        onClose={() => setCropOpen(null)}
      />
    </div>
  );
}
